/**
 * The company website row in the Add link sheet.
 *
 * The website used to live in the Edit profile form, which saved every identity
 * field in one submit. Here it is the only thing on the row, so it commits on
 * its own the moment the input is committed — the same shape as the social rows
 * beside it, which autosave through the builder.
 *
 * It is not a social: it persists to `Company.website` via the workspace API,
 * not to the socials list, and the Website/Visit/Map blocks read that field.
 */
import { useCallback, useState } from 'react';
import { useWorkspace } from '../../providers/useWorkspace';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';

export interface CompanyWebsiteField {
  value: string;
  isSaving: boolean;
  /** `''` clears the website; the API stores `null` for an empty value. */
  commit: (next: string) => void;
}

export function useCompanyWebsiteField(): CompanyWebsiteField {
  const { draftProfile, setDraftProfile } = usePublicProfileBuilderModel();
  const { companyId, updateCompanyWorkspace } = useWorkspace();
  const [isSaving, setIsSaving] = useState(false);

  const value = draftProfile?.website ?? '';

  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      if (trimmed === value.trim()) return;
      if (!companyId) return;

      // Optimistic: the hero link and the Website block both read the draft, so
      // waiting for the round trip would make the page look like it ignored the
      // edit. A failed save restores the previous value.
      setDraftProfile({ website: trimmed });
      setIsSaving(true);
      void updateCompanyWorkspace(companyId, { website: trimmed || null })
        .catch(() => {
          setDraftProfile({ website: value });
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [companyId, setDraftProfile, updateCompanyWorkspace, value],
  );

  return { value, isSaving, commit };
}
